import type { TableType, TableRowType } from '../../../grid/reduxStore/table.types';
import { RowTypesIdentifier, TableTypeIdentifier, ColumnTypeIdentifier } from '../../../grid/reduxStore/table.types';
import { rootStore, useAppDispatch } from '../../../grid/reduxStore/Store';
import { useBlockDimensionChangedHandlerWithoutUndoRedo } from '../../../hooks/UseBlockDimensionChangedHandler';
import type { GridApiCommon, GridApiPro, GridCellParams } from '@mui/x-data-grid-pro';
import { useTableContentUndoRedo } from '../../../hooks/useTableContentUndoRedo.ts';
import type { BlockContent } from '../../../grid/reduxStore/editorSlice';
import { updateGridTableBlockState, updateGridDimensionConfig, selectSectionById } from '../../../grid/reduxStore/editorSlice';
import { getSanitizedDataForTableFooter } from '../../../../../utils/getSanitizedDataForTableFooter';
import { setOfRowsForPricingTable, setOfRowsForPricingTableWithReferences } from './defaultData';
import useFeatureFlag from '../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../utils/featureFlags.ts';
import { useTableTotals } from './useTableTotals';
import { SIZES } from '../../../../../muiTheme/MuiDataGrid';

const REORDER_COLUMN_FIELD = '__reorder__';

export const useTableManipulation = () => {
  const dispatch = useAppDispatch();
  const blockDimensionChangedHandlerWithoutUndoRedo = useBlockDimensionChangedHandlerWithoutUndoRedo();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const isCurrencySettingsEnabled = useFeatureFlag([FeatureFlags.currencySettings])[FeatureFlags.currencySettings];
  const getTableTotals = useTableTotals();
  const getBlockContent = (blockId: string, sectionId: string): BlockContent | null => {
    return { ...(selectSectionById(rootStore.getState(), sectionId)?.blocksContent[blockId] as BlockContent) };
  };

  const getTableData = (blockId: string, sectionId: string): TableType | undefined => {
    const blockContent = getBlockContent(blockId, sectionId);
    if (!blockContent) return undefined;
    return blockContent.contentTable;
  };

  const getUpdatedRowsTableDataFromCellEdit = (cellParams: GridCellParams, newContent: string, blockId: string, sectionId: string) => {
    const tableData = getTableData(blockId, sectionId);
    if (!tableData) return;

    const subtotalColumn = tableData.columns.find((col) => col.columnType === ColumnTypeIdentifier.SUBTOTAL);
    const isSubtotalEdit = !!subtotalColumn && cellParams.field === subtotalColumn.field;

    const updatedRows: TableRowType[] = tableData.rows.map((row): TableRowType => {
      if (row.id === cellParams.id && row.rowType !== RowTypesIdentifier.FOOTER) {
        return {
          ...row,
          [cellParams.field]: newContent,
          ...(isSubtotalEdit && { manualSubtotalOverride: newContent }),
        };
      } else if (row.id === cellParams.id && row.rowType === RowTypesIdentifier.FOOTER) {
        const convertedRowToFooterData = getSanitizedDataForTableFooter(row, tableData.columns, newContent);
        return { ...convertedRowToFooterData, rowType: RowTypesIdentifier.FOOTER };
      } else {
        return { ...row };
      }
    });
    const { rows, totalRows, metadata } = getTableTotals(updatedRows, tableData.columns, tableData);
    const nextTableData: TableType = {
      ...tableData,
      rows,
      totalRows,
      metadata,
    };

    return nextTableData;
  };

  const getTableUpdatedDimensions = (tableApi: GridApiPro | GridApiCommon) => {
    if (!tableApi || !tableApi.rootElementRef.current) return undefined;
    const visibleColumns = tableApi.getVisibleColumns();

    // MUI injects a __reorder__ column and reveals the column-header row only while
    // the grid is in edit mode. Both are transient edit-mode chrome and must not
    // contribute to the block's persisted dimensions, otherwise the value drifts by
    // 50px width / 33px height on every edit-mode toggle and leaks into preview/PDF.
    const isEditChromeVisible = visibleColumns.some((column) => column.field === REORDER_COLUMN_FIELD);
    const columnsTotalWidth = visibleColumns
      .filter((column) => column.field !== REORDER_COLUMN_FIELD)
      .reduce((width, column) => width + (column.width ?? 0), 0);
    const rawHeight = tableApi.rootElementRef.current.offsetHeight as number;
    const height = isEditChromeVisible ? rawHeight - SIZES.columnHeaderHeight : rawHeight;

    return {
      width: columnsTotalWidth,
      height,
    };
  };

  const updateBlockDimensionsWithTableDimensions = async (blockId: string, sectionId: string, tableApi: GridApiPro | GridApiCommon) => {
    const dimensions = getTableUpdatedDimensions(tableApi);
    if (!dimensions) return;

    await blockDimensionChangedHandlerWithoutUndoRedo(sectionId, blockId, { widthPx: dimensions.width, heightPx: dimensions.height });
  };

  const setTableDecimalPlaces = async (blockId: string, sectionId: string, updatedDecimalPlaces: number) => {
    const tableData = getTableData(blockId, sectionId);
    if (!tableData) return;
    const { metadata } = tableData;
    const updatedTableData = {
      ...tableData,
      metadata: {
        ...metadata,
        pricingDecimalPlaces: updatedDecimalPlaces,
      },
    } as TableType;
    handleTableContentChanged(blockId, sectionId, tableData, updatedTableData);
  };

  const addCustomFieldToRow = ({
    blockId,
    sectionId,
    field,
    id,
    cellConfigObject,
  }: {
    blockId: string;
    sectionId: string;
    field: string;
    id: string;
    cellConfigObject: Record<string, string | number | boolean>;
  }) => {
    const tableContentData = getTableData(blockId, sectionId);
    if (!tableContentData) return;

    const updatedRowsData: TableRowType[] = tableContentData.rows.map((row): TableRowType => {
      const customField = row.cellConfig && { ...row.cellConfig?.[field] };
      return row.id === id ? { ...row, cellConfig: { ...row.cellConfig, [field]: { ...customField, ...cellConfigObject } } } : row;
    });
    return {
      ...tableContentData,
      rows: updatedRowsData,
    };
  };

  const addSlotRowField = ({
    blockId,
    sectionId,
    field,
    id,
    cellConfig,
  }: {
    blockId: string;
    sectionId: string;
    field: string;
    id: string;
    cellConfig: Record<string, string | number | boolean>;
  }): TableRowType[] | undefined => {
    const totalRows = getTableData(blockId, sectionId)?.totalRows;
    if (!totalRows) return;

    return totalRows.map((row): TableRowType => {
      return row.id === id ? { ...row, cellConfig: { ...row.cellConfig, price: { ...row.cellConfig?.[field], ...cellConfig } } } : row;
    });
  };

  const toggleTableType = (blockId: string, sectionId: string) => {
    const tableContentData = getTableData(blockId, sectionId);

    if (!tableContentData) return;

    const { metadata } = tableContentData;
    const isPricingActive = metadata?.tableType === TableTypeIdentifier.PRICING_TABLE;
    const rowsForPricingTable = isCurrencySettingsEnabled ? setOfRowsForPricingTableWithReferences : setOfRowsForPricingTable;

    return {
      ...tableContentData,
      totalRows: isPricingActive ? [] : rowsForPricingTable,
      metadata: {
        ...tableContentData.metadata,
        tableType: isPricingActive ? TableTypeIdentifier.TEXT_TABLE : TableTypeIdentifier.PRICING_TABLE,
      },
    };
  };

  const updateTableBlockContentStoreState = (blockId: string, sectionId: string, tableData: TableType) => {
    const payload = {
      sectionId: sectionId,
      blockId: blockId,
      contentTable: tableData,
    };
    return dispatch(updateGridTableBlockState(payload));
  };

  const updateTableBlockDimensionsStoreState = (
    blockId: string,
    sectionId: string,
    { width, height }: { width: number; height: number }
  ) => {
    dispatch(updateGridDimensionConfig({ sectionId, blockId, width, height }));
  };

  return {
    getBlockContent,
    getTableData,
    getTableUpdatedDimensions,
    updateBlockDimensionsWithTableDimensions,
    toggleTableType,
    setTableDecimalPlaces,
    updateTableBlockContentStoreState,
    updateTableBlockDimensionsStoreState,
    getUpdatedRowsTableDataFromCellEdit,
    addCustomFieldToRow,
    addSlotRowField,
  };
};
